import * as vp from 'vopay';
import {
  Box,
  Select,
  TextField,
  MultiSelect,
  DatepickerSimple,
  TextArea,
  CardCheckbox,
  MaterialSymbol,
  Radio,
  Tooltip,
  withStyles,
  InteracWordmark,
  toBool,
  FieldData,
} from 'vopay';
import * as Yup from 'yup';
import { format, addDays, isBefore, startOfDay, isEqual, isAfter } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { CreateReportRequest, CreateReportFormData } from './types';
import styles from './buildCreateReportsFields.module.scss';
import { Button, EmailField } from 'UI';
import { AccountTransactionReportType } from 'vopay';

export type BasicOptions = {
  label: string;
  value: string;
  isVisible?: boolean;
};

const classes = withStyles(styles);

export function buildCreateReportDetailsFields(
  subscriptions?: vp.SubscriptionItem[],
): FieldData<CreateReportFormData>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.Form' });
  const { response } = useClientData();
  type AccountTransactionReportTypeOptionType = {
    label: string;
    value: AccountTransactionReportType;
    show: boolean;
  };

  const accountTransactionReportTypeOptions: AccountTransactionReportTypeOptionType[] = [
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.approvalActivity', {
        defaultValue: 'Approval Activity',
      }),
      value: 'ApprovalActivityReport',
      show: toBool(response.TransactionApprovalWorkflowEnabled),
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.accountTransactionHistory', {
        defaultValue: 'Account Transaction History',
      }),
      value: 'AccountTransactionHistoryReport',
      show: true,
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.accountTransactionSummary'),
      value: 'AccountTransactionSummary',
      show: true,
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.accountingSoftwareActivityReport'),
      value: 'AccountingSoftwareActivityReport',
      show: response.AccountingSoftwareIntegrationEnabled,
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.endOfDayBalance'),
      value: 'EndOfDayBalance',
      show: true,
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.interestReport'),
      value: 'InterestReport',
      show: response.InterestAccountEnabled,
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.transactionsPerPaymentRail'),
      value: 'TransactionsPerPaymentRail',
      show: true,
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.transactionSuccessAndFailureAnalysisReport'),
      value: 'TransactionSuccessAndFailureAnalysisReport',
      show: true,
    },
    { label: t('SelectOptions.AccountTransactionReportTypeOptions.invoiceReport'), value: 'InvoiceReport', show: true },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.glCodeTransactionSummary'),
      value: 'GLCodeTransactionSummary',
      show: true,
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.glCodeSummaryReport'),
      value: 'GLCodeSummaryReport',
      show: true,
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.transactionSummaryByCode'),
      value: 'TransactionSummaryByCode',
      show: true,
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.transactionCodeSummaryReport'),
      value: 'TransactionCodeSummary',
      show: true,
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.transactionFailureDetailReport'),
      value: 'TransactionFailureDetailReport',
      show: response.Country === 'US' ? toBool(response.ACHWithdrawEnabled) : toBool(response.EFTSendEnabled),
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.paymentMethodChangeLogReport', {
        defaultValue: 'Payment Method Change Log',
      }),
      value: 'PaymentMethodChangeLogReport',
      show: true,
    },
  ];

  const descriptions: Record<(typeof accountTransactionReportTypeOptions)[number]['value'], string> = {
    ApprovalActivityReport: t('AccountTransactionReportDescriptions.approvalActivity', {
      defaultValue:
        'Provides a detailed record of all transactions that required approval, including the actions taken by each approver',
    }),
    AccountTransactionHistoryReport: t('AccountTransactionReportDescriptions.accountTransactionHistory', {
      defaultValue:
        'This report provides a complete record of all transactions associated with the account over a specified period, including transaction dates, amounts, types, statuses, and reference details.',
    }),
    AccountTransactionSummary: t('AccountTransactionReportDescriptions.accountTransactionSummary'),
    AccountingSoftwareActivityReport: t('AccountTransactionReportDescriptions.accountingSoftwareActivityReport'),
    EndOfDayBalance: t('AccountTransactionReportDescriptions.endOfDayBalance'),
    InterestReport: t('AccountTransactionReportDescriptions.interestReport'),
    TransactionsPerPaymentRail: t('AccountTransactionReportDescriptions.transactionsPerPaymentRail'),
    TransactionSuccessAndFailureAnalysisReport: t(
      'AccountTransactionReportDescriptions.transactionSuccessAndFailureAnalysisReport',
    ),
    InvoiceReport: t('AccountTransactionReportDescriptions.invoiceReport'),
    GLCodeTransactionSummary: t('AccountTransactionReportDescriptions.glCodeTransactionSummary'),
    GLCodeSummaryReport: t('AccountTransactionReportDescriptions.glCodeSummaryReport'),
    TransactionSummaryByCode: t('AccountTransactionReportDescriptions.transactionSummaryByCode'),
    TransactionCodeSummary: t('AccountTransactionReportDescriptions.transactionCodeSummaryReport'),
    TransactionFailureDetailReport: t('AccountTransactionReportDescriptions.transactionFailureDetailReport'),
    PaymentMethodChangeLogReport: t('AccountTransactionReportDescriptions.paymentMethodChangeLogReport', {
      defaultValue:
        'Audit log of payment method changes (add, update, delete, default change) across contacts and client accounts, with masked identifiers, for a selected date range.',
    }),
  };

  return [
    {
      label: () => t('Label.recipientEmails'),
      fieldName: 'RecipientEmails',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) =>
        values.RecipientEmails.split(',')
          .filter((a) => a.trim() !== '')
          .join(','),
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({});
        const recipientEmails = ctx.values.RecipientEmails.split(',');

        return (
          <EmailField
            emails={recipientEmails}
            onAdd={(newEmail) => {
              ctx.setFieldValue('RecipientEmails', ctx.values.RecipientEmails + ',' + newEmail);
            }}
            onRemove={(email) => {
              ctx.setFieldValue('RecipientEmails', recipientEmails.filter((m) => m !== email).join(','));
            }}
            {...fieldData}
          />
        );
      },
      validate: () =>
        Yup.string()
          .test('is-valid-emails', t('ValidationMessage.recipientEmails'), (value) => {
            if (!value) return false;
            const emails = value.split(',');

            return emails.every((email) => Yup.string().email().isValidSync(email.trim()));
          })
          .required(),
    },
    {
      label: () => t('Label.reportName'),
      fieldName: 'ReportName',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.ReportName?.replace(/, /g, ' ').replace(/,/g, ''),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" placeholder={t('Placeholder.reportName')} {...fieldData} />;
      },
      validate: () =>
        Yup.string()
          .test('unique-name-validation', t('ValidationMessage.ReportName.unique'), (value) => {
            const check = Object.values(subscriptions || {}).some(
              (data) => data.ReportName.toLowerCase() === value?.toLocaleLowerCase(),
            );

            return !check;
          })
          .max(45, t('ValidationMessage.ReportName.characterLimit'))
          .matches(
            /^[A-Za-z0-9_ ]+$/,
            t('ValidationMessage.ReportName.noSpecialCharacter', {
              defaultValue: "This field cannot contain any of special characters. eg) ? ! / < ~ ` ' > <",
            }),
          )
          .trim()
          .required(),
    },
    {
      label: () => t('Label.reportType'),
      fieldName: 'AccountTransactionReportType',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.AccountTransactionReportType,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e) => {
            const type = e.target.value;
            const description = descriptions[type as keyof typeof descriptions];

            if (description) {
              ctx.setFieldValue('Description', description);
            }
          },
        });

        return (
          <Select
            version="2"
            placeholder={t('Placeholder.select')}
            options={accountTransactionReportTypeOptions.filter((option) => option.show)}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('Label.description'),
      fieldName: 'Description',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.Description,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextArea
            height="90px"
            placeholder={t('Placeholder.description')}
            maxLength={220}
            showcaseLength
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
  ];
}

export function buildCreateReportFrequencyFields(
  disabledFields: (keyof CreateReportRequest)[],
): FieldData<CreateReportFormData>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.Form' });

  const dateRangeOptions = [
    {
      value: 'CustomDateRange',
      label: t('SelectOptions.DateRangeOptions.customDateRange'),
    },
    {
      value: 'Today',
      label: t('SelectOptions.DateRangeOptions.today'),
    },
    {
      value: 'Yesterday',
      label: t('SelectOptions.DateRangeOptions.yesterday'),
    },
    {
      value: 'Last7Days',
      label: t('SelectOptions.DateRangeOptions.last7Days'),
    },
    {
      value: 'Last30Days',
      label: t('SelectOptions.DateRangeOptions.last30Days'),
    },
    {
      value: 'ThisWeek',
      label: t('SelectOptions.DateRangeOptions.thisWeek'),
    },
    {
      value: 'LastWeek',
      label: t('SelectOptions.DateRangeOptions.lastWeek'),
    },
    {
      value: 'ThisMonth',
      label: t('SelectOptions.DateRangeOptions.thisMonth'),
    },
    {
      value: 'LastMonth',
      label: t('SelectOptions.DateRangeOptions.lastMonth'),
    },
    {
      value: 'ThisQuarter',
      label: t('SelectOptions.DateRangeOptions.thisQuarter'),
    },
    {
      value: 'LastQuarter',
      label: t('SelectOptions.DateRangeOptions.lastQuarter'),
    },
    {
      value: 'YearToDate',
      label: t('SelectOptions.DateRangeOptions.yearToDate'),
    },
    {
      value: 'LastYear',
      label: t('SelectOptions.DateRangeOptions.lastYear'),
    },
  ];

  const scheduleTypeOptions = {
    'one-time': t('SelectOptions.ScheduleTypeOptions.oneTime'),
    subscription: t('SelectOptions.ScheduleTypeOptions.subscription'),
  };

  return [
    {
      fieldName: 'ScheduleType',
      showField: () => true,
      getInputState: () => 'optional',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <Radio
            variant="outlineShadow"
            options={scheduleTypeOptions}
            noOrdering
            {...fieldData}
            disabled={disabledFields.includes('ScheduleType')}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      fieldName: 'Frequency',
      getInputState: () => 'optional',
      showField: (values) => values.ScheduleType === 'subscription',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <Radio
            variant="outlineShadow"
            options={{
              daily: (
                <Box ai="center" gap="sm">
                  <span>{t('SelectOptions.FrequencyOptions.daily')}</span>
                  <Tooltip type="hover" information={t('ToolTipMessage.daily')} position="top" hasArrow icon="info" />
                </Box>
              ),
              weekly: (
                <Box ai="center" gap="sm">
                  <span>{t('SelectOptions.FrequencyOptions.weekly')}</span>
                  <Tooltip type="hover" information={t('ToolTipMessage.weekly')} position="top" hasArrow icon="info" />
                </Box>
              ),
              monthly: (
                <Box ai="center" gap="sm">
                  <span>{t('SelectOptions.FrequencyOptions.monthly')}</span>
                  <Tooltip type="hover" information={t('ToolTipMessage.monthly')} position="top" hasArrow icon="info" />
                </Box>
              ),
            }}
            noOrdering
            {...fieldData}
            disabled={disabledFields.includes('Frequency')}
          />
        );
      },
      validate: () =>
        Yup.string().when(['ScheduleType'], {
          is: (ScheduleType: string) => ScheduleType === 'subscription',
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('Label.dateRangeOptions'),
      fieldName: 'DateRangeOption',
      getInputState: () => 'required',
      showField: (values) => values.ScheduleType === 'one-time',
      serializeField: (values) => values.DateRangeOption,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <Select version="2" placeholder={t('Placeholder.select')} options={dateRangeOptions} {...fieldData} />;
      },
      validate: () =>
        Yup.string().when(['ScheduleType'], {
          is: (ScheduleType: string) => ScheduleType === 'one-time',
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('Label.from'),
      fieldName: 'FromDate',
      getInputState: () => 'required',
      showField: (values) => values.ScheduleType === 'one-time' && values.DateRangeOption === 'CustomDateRange',
      serializeField: (values) => {
        if (values.FromDate && isToday(values.FromDate)) {
          return undefined;
        }

        const startDate = new Date(Number(values.FromDate));

        return format(startDate, 'yyyy-MM-dd');
      },
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const selectedDate = new Date(Number(e.target.value));
            const endDate = new Date(Number(ctx.values.ToDate));

            if (
              isAfter(selectedDate, endDate) ||
              format(selectedDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')
            ) {
              const newDate = addDays(selectedDate, 1).getTime();
              ctx.setFieldValue('ToDate', newDate.toString());
            }
          },
        });

        return (
          <DatepickerSimple
            variant="outlineShadow"
            isDisabled={fieldData.disabled}
            iconPosition="left"
            {...fieldData}
          />
        );
      },
      validate: () =>
        Yup.string().when(['DateRangeOption'], {
          is: (DateRangeOption: string) => DateRangeOption === 'CustomDateRange',
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('Label.to'),
      fieldName: 'ToDate',
      getInputState: () => 'required',
      showField: (values) => values.ScheduleType === 'one-time' && values.DateRangeOption === 'CustomDateRange',
      serializeField: (values) => {
        const endDate = new Date(Number(values.ToDate));

        return format(endDate, 'yyyy-MM-dd');
      },
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <DatepickerSimple
            variant="outlineShadow"
            min={addDays(new Date(Number(ctx.values.FromDate)), 1).getTime() as vp.Timestamp}
            iconPosition="left"
            placeholder=""
            {...fieldData}
            isDisabled={fieldData.disabled}
          />
        );
      },
      validate: (ctx) =>
        Yup.string().when(['DateRangeOption'], {
          is: (DateRangeOption: string) => DateRangeOption === 'CustomDateRange',
          then: Yup.string()
            .required()
            .test('end-date-validation', t('ValidationMessage.toDate'), (value: string | undefined) => {
              const endDate = value ? new Date(Number(value)) : undefined;
              const startDate = new Date(Number(ctx.values.FromDate));
              if (!endDate) return false;

              return !isEqual(endDate, startDate) && !isBefore(endDate, startDate);
            }),
          otherwise: Yup.string(),
        }),
    },
  ];
}

export function buildCreateReportConfigFields(): FieldData<CreateReportFormData>[] {
  const {
    response: { Country },
  } = useClientData();

  const { permissions } = usePermissions();
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.Form' });

  const DateFormatOptions = Object.entries(vp.SUBSCRIPTION_DATE_FORMAT_SELECT_OPTIONS).map(([key, value]) => ({
    label: key,
    value,
  }));

  return [
    {
      label: () => t('Label.paymentMethods'),
      fieldName: 'PaymentMethods',
      getInputState: () => 'required',
      showField: (values) => {
        if (!values.AccountTransactionReportType) return false;

        const whitelist: AccountTransactionReportType[] = [
          'AccountTransactionHistoryReport',
          'AccountTransactionSummary',
          'TransactionsPerPaymentRail',
        ];

        return whitelist.includes(values.AccountTransactionReportType);
      },
      serializeField: (values) => values.PaymentMethods.join(','),
      renderField: ({ ctx, getFormikData }) => {
        const multiSelectFieldData = getFormikData<string[]>({
          onChange: (e) => {
            const value = e.target.value;
            ctx.setFieldValue('PaymentMethods', value === '' ? [] : value.split(','));
          },
        });

        const options =
          Country === 'US'
            ? [
                { value: 'Reversal', label: t('SelectOptions.PaymentMethodOptions.reversal') },
                { value: 'Credit Card', label: t('SelectOptions.PaymentMethodOptions.creditCard') },
                { value: 'Google Pay', label: t('SelectOptions.PaymentMethodOptions.googlePay') },
                { value: 'PayPal Withdrawal', label: t('SelectOptions.PaymentMethodOptions.payPalWithdrawal') },
                { value: 'Venmo Withdrawal', label: t('SelectOptions.PaymentMethodOptions.venmoWithdrawal') },
                {
                  value: 'Zelle Withdrawal',
                  label: t('SelectOptions.PaymentMethodOptions.zelleWithdrawal', { defaultValue: 'Zelle Withdrawal' }),
                },
                { value: 'Refund', label: t('SelectOptions.PaymentMethodOptions.refund') },
                { value: 'ACH Funding', label: t('SelectOptions.PaymentMethodOptions.achFunding') },
                { value: 'ACH Withdrawal', label: t('SelectOptions.PaymentMethodOptions.achWithdrawal') },
                { value: 'RTP Withdrawal', label: t('SelectOptions.PaymentMethodOptions.rtpWithdrawal') },
                { value: 'OCT', label: t('SelectOptions.PaymentMethodOptions.oct') },
                { value: 'Fedwire Funding', label: t('SelectOptions.PaymentMethodOptions.fedwireFunding') },
                { value: 'Wire', label: t('SelectOptions.PaymentMethodOptions.wire') },
                { value: 'eLinx', label: t('SelectOptions.PaymentMethodOptions.elinx', { defaultValue: 'eLinx®' }) },
              ]
            : [
                { value: 'EFT Withdrawal', label: t('SelectOptions.PaymentMethodOptions.eftWithdrawal') },
                { value: 'EFT Funding', label: t('SelectOptions.PaymentMethodOptions.eftFunding') },
                { value: 'VoPayInstant Withdraw', label: t('SelectOptions.PaymentMethodOptions.vopayInstantWithdraw') },
                { value: 'Refund', label: t('SelectOptions.PaymentMethodOptions.refund') },
                { value: 'Currency Conversion', label: t('SelectOptions.PaymentMethodOptions.currencyConversion') },
                {
                  value: 'Interac Money Request',
                  label: t('SelectOptions.PaymentMethodOptions.interacMoneyRequest'),
                  template: <InteracWordmark type="moneyRequest" />,
                },
                {
                  value: 'Interac Bulk Payout',
                  label: t('SelectOptions.PaymentMethodOptions.interacBulkPayout'),
                  template: <InteracWordmark type="bulkPayout" />,
                },
                {
                  value: 'Inbound e-Transfer',
                  label: t('SelectOptions.PaymentMethodOptions.inboundETransfer'),
                  template: <InteracWordmark type="inbound" />,
                },
                { value: 'Visa Direct Push', label: t('SelectOptions.PaymentMethodOptions.visaDirectPush') },
                { value: 'Credit Card', label: t('SelectOptions.PaymentMethodOptions.creditCard') },
                { value: 'Google Pay', label: t('SelectOptions.PaymentMethodOptions.googlePay') },
                { value: 'PayPal Withdrawal', label: t('SelectOptions.PaymentMethodOptions.payPalWithdrawal') },
                { value: 'Account Transfer', label: t('SelectOptions.PaymentMethodOptions.accountTransfer') },
                { value: 'Reversal', label: t('SelectOptions.PaymentMethodOptions.reversal') },
                { value: 'Bill Pay', label: t('SelectOptions.PaymentMethodOptions.billPay') },
                {
                  value: 'Virtual Account Funding',
                  label: t('SelectOptions.PaymentMethodOptions.virtualAccountFunding'),
                },
                {
                  value: 'Virtual Account Withdraw',
                  label: t('SelectOptions.PaymentMethodOptions.virtualAccountWithdraw'),
                },
                { value: 'Virtual Account Wire', label: t('SelectOptions.PaymentMethodOptions.virtualAccountWire') },
                { value: 'eLinx', label: t('SelectOptions.PaymentMethodOptions.elinx', { defaultValue: 'eLinx®' }) },
              ];

        return (
          <>
            <MultiSelect
              placeholder={t('Placeholder.select')}
              renderSelectedValue={(_, { Placeholder }) => <Placeholder />}
              options={options}
              renderOption={(o, { Option, SelectedIcon, selected }) => (
                <Option>
                  {o.template || o.label}
                  {selected && <SelectedIcon />}
                </Option>
              )}
              renderDropdownFooter={() => {
                return options.every((option) => option.value && ctx.values.PaymentMethods.includes(option.value)) ? (
                  <div className={classes('paymentMethodsFooter')}>
                    <Button
                      onClick={() => {
                        ctx.setFieldValue('PaymentMethods', []);
                      }}
                      size="small"
                    >
                      {t('Label.deselectAll')}
                    </Button>
                  </div>
                ) : (
                  <div className={classes('paymentMethodsFooter')}>
                    <Button
                      onClick={() => {
                        const newSelected = [...ctx.values.PaymentMethods];

                        options.forEach((option) => {
                          // The field stores raw API values — pushing labels here
                          // would submit display text as the rail.
                          if (!option.value) return;

                          if (!newSelected.includes(option.value)) {
                            newSelected.push(option.value);
                          }
                        });

                        ctx.setFieldValue('PaymentMethods', newSelected);
                      }}
                      size="small"
                    >
                      {t('Label.selectAll')}
                    </Button>
                  </div>
                );
              }}
              {...multiSelectFieldData}
            />

            {!!ctx.values.PaymentMethods.length && (
              <div className={classes('multiselectOptionsContainer')}>
                {ctx.values.PaymentMethods.map((m) => (
                  <div key={m} className={classes('multiselectOption')}>
                    {/* PaymentMethods holds raw API values; show the option's
                        display form (the wordmark for Interac rails) instead. */}
                    <span>
                      {options.find((o) => o.value === m)?.template || options.find((o) => o.value === m)?.label || m}
                    </span>

                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const filteredList = ctx.values.PaymentMethods.filter((a) => {
                          return m !== a;
                        });

                        ctx.setFieldValue('PaymentMethods', filteredList);
                      }}
                    >
                      <MaterialSymbol icon="close" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        );
      },
      validate: (ctx) =>
        Yup.mixed().when('AccountTransactionReportType', {
          is: () => {
            if (!ctx.values.AccountTransactionReportType) return false;

            const whitelist: AccountTransactionReportType[] = [
              'AccountTransactionHistoryReport',
              'AccountTransactionSummary',
              'TransactionsPerPaymentRail',
            ];

            return whitelist.includes(ctx.values.AccountTransactionReportType);
          },
          then: Yup.array().min(1, t('ValidationMessage.paymentRails')),
          otherwise: Yup.array(),
        }),
    },
    {
      label: () => t('Label.dateFormat'),
      fieldName: 'DateFormat',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.DateFormat,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <Select version="2" placeholder={t('Placeholder.select')} options={DateFormatOptions} {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('Label.failureReason'),
      fieldName: 'TransactionReportFailureReasons',
      getInputState: () => 'required',
      showField: (values) => values.AccountTransactionReportType === 'TransactionFailureDetailReport',
      renderField: ({ ctx, getFormikData }) => {
        const multiSelectFieldData = getFormikData<string[]>({
          onChange: (e) => {
            const value = e.target.value;
            ctx.setFieldValue('TransactionReportFailureReasons', value === '' ? [] : value.split(','));
          },
        });

        const options = [
          {
            value: 'nsf',
            label: t('SelectOptions.FailureReasonOptions.nsf'),
            description: t('SelectOptions.FailureReasonOptions.Description.nsf'),
          },
          {
            value: 'unauthorized',
            label: t('SelectOptions.FailureReasonOptions.unauthorized'),
            description: t('SelectOptions.FailureReasonOptions.Description.unauthorized'),
          },
          {
            value: 'administrative',
            label: t('SelectOptions.FailureReasonOptions.administrative'),
            description: t('SelectOptions.FailureReasonOptions.Description.administrative'),
          },
        ];

        return (
          <>
            <MultiSelect
              filterable
              placeholder={t('Placeholder.select')}
              renderSelectedValue={(_, { Placeholder }) => <Placeholder />}
              options={options}
              renderOption={(o, { Option, SelectedIcon, selected }) => (
                <Option>
                  <Box ai="center" jc="sb" width="100%">
                    <Box flx="col">
                      {o.label}
                      <div className={classes('failureReasonDescription')}>{o.description}</div>
                    </Box>

                    {selected && <SelectedIcon />}
                  </Box>
                </Option>
              )}
              {...multiSelectFieldData}
            />

            {!!ctx.values.TransactionReportFailureReasons.length && (
              <div className={classes('multiselectOptionsContainer')}>
                {ctx.values.TransactionReportFailureReasons.map((m) => (
                  <div key={m} className={classes('multiselectOption')}>
                    <span>{options.find((option) => option.value === m)?.label}</span>

                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const filteredList = ctx.values.TransactionReportFailureReasons.filter((a) => {
                          return m !== a;
                        });

                        ctx.setFieldValue('TransactionReportFailureReasons', filteredList);
                      }}
                    >
                      <MaterialSymbol icon="close" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        );
      },
      validate: (ctx) =>
        Yup.mixed().when('AccountTransactionReportType', {
          is: () => ctx.values.AccountTransactionReportType === 'TransactionFailureDetailReport',
          then: Yup.array().min(1, t('ValidationMessage.failureReason')),
          otherwise: Yup.array(),
        }),
    },
    {
      fieldName: 'IncludeSubAccounts',
      getInputState: () => 'optional',
      showField: (values) => {
        if (!values.AccountTransactionReportType) return false;

        const whitelist: AccountTransactionReportType[] = [
          'AccountTransactionHistoryReport',
          'AccountTransactionSummary',
          'EndOfDayBalance',
          'TransactionsPerPaymentRail',
          'TransactionSuccessAndFailureAnalysisReport',
          'InvoiceReport',
          'InterestReport',
          'GLCodeTransactionSummary',
          'GLCodeSummaryReport',
          'TransactionSummaryByCode',
          'TransactionCodeSummary',
          'PaymentMethodChangeLogReport',
        ];

        return !!permissions.ViewSubaccounts && whitelist.includes(values.AccountTransactionReportType);
      },
      serializeField: (values) => values.IncludeSubAccounts,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});

        return (
          <CardCheckbox
            indicator="switch"
            checked={fieldData.value}
            label={<Box gapSize="4px">{t('Label.includeSubAccounts')}</Box>}
            {...fieldData}
          />
        );
      },
    },
    {
      fieldName: 'IncludeGraph',
      getInputState: () => 'optional',
      showField: (values) => {
        if (!values.AccountTransactionReportType) return false;

        const whitelist: AccountTransactionReportType[] = [
          'AccountTransactionSummary',
          'EndOfDayBalance',
          'TransactionsPerPaymentRail',
          'TransactionSuccessAndFailureAnalysisReport',
          'InvoiceReport',
          'InterestReport',
          'TransactionSummaryByCode',
          'TransactionCodeSummary',
        ];

        return whitelist.includes(values.AccountTransactionReportType);
      },
      serializeField: (values) => values.IncludeGraph,
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<boolean>({});

        const isDisabled = ['GLCodeTransactionSummary', 'GLCodeSummaryReport'].includes(
          ctx.values.AccountTransactionReportType,
        );

        return (
          <CardCheckbox
            indicator="switch"
            checked={!isDisabled ? fieldData.value : false}
            label={<Box gapSize="4px">{t('Label.includeGraph')}</Box>}
            {...fieldData}
          />
        );
      },
    },
  ];
}

function isToday(dateStr: string): boolean {
  const date = startOfDay(new Date(Number(dateStr)));
  const today = startOfDay(new Date());

  return isEqual(date, today);
}
